import React, { useState } from "react";
import { useQuery } from "react-query";
import { cn } from "@/lib/utils";
import { inputClass } from "@/lib/formStyles";
import { getPatientsPaginated } from "../../services/users";
import { hm, initials, TodayAppointmentApi } from "../../services/queue/types";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import type { QueueCandidate } from "./AddToQueueModal";

const MIN_SEARCH_LENGTH = 2;
const RESULTS_LIMIT = 8;

type PatientSearchStepProps = {
  appointmentsByPatient: Map<number, TodayAppointmentApi>;
  /** Patients déjà dans la file du jour : affichés mais non sélectionnables. */
  registeredPatientIds: Set<number>;
  onSelect: (patient: QueueCandidate) => void;
};

/** Première étape de l'ajout depuis la liste d'attente : retrouver le patient. */
const PatientSearchStep: React.FC<PatientSearchStepProps> = ({
  appointmentsByPatient,
  registeredPatientIds,
  onSelect,
}) => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim());
  const searchEnabled = debouncedSearch.length >= MIN_SEARCH_LENGTH;

  const { data, isFetching } = useQuery(
    ["queue-patient-search", debouncedSearch],
    () => getPatientsPaginated(debouncedSearch, 0, RESULTS_LIMIT),
    { enabled: searchEnabled, keepPreviousData: true }
  );
  const patients: any[] = searchEnabled ? (data as any)?.data?.content || [] : [];

  return (
    <div className="space-y-3">
      <label className="group block">
        <span className="mb-1.5 block text-sm font-medium text-slate-700">Patient</span>
        <div className="relative">
          <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-lg text-slate-400 group-focus-within:text-emerald-600">
            <i className="bx bx-search" aria-hidden />
          </span>
          <input
            type="text"
            autoFocus
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Nom, prénom, téléphone…"
            className={cn(inputClass, "mt-0 pl-11 pr-4")}
          />
        </div>
      </label>

      {!searchEnabled && (
        <p className="text-xs text-slate-400">Tapez au moins {MIN_SEARCH_LENGTH} caractères.</p>
      )}
      {searchEnabled && !isFetching && patients.length === 0 && (
        <p className="text-sm text-slate-500">Aucun patient trouvé.</p>
      )}

      <ul className="max-h-72 space-y-1 overflow-y-auto">
        {patients.map((patient) => {
          const patientId = Number(patient.id);
          const alreadyRegistered = registeredPatientIds.has(patientId);
          const appointment = appointmentsByPatient.get(patientId);
          return (
            <li key={patientId}>
              <button
                type="button"
                disabled={alreadyRegistered}
                onClick={() => onSelect(patient)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors",
                  alreadyRegistered ? "cursor-not-allowed opacity-50" : "hover:bg-emerald-50/60"
                )}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-xs font-semibold text-white">
                  {initials(patient.firstName, patient.lastName)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-800">
                    {patient.firstName} <span className="uppercase">{patient.lastName}</span>
                  </span>
                  <span className="block truncate text-xs text-slate-500">{patient.phoneNumber || "—"}</span>
                </span>
                {alreadyRegistered ? (
                  <span className="text-xs font-medium text-slate-500">Déjà dans la liste</span>
                ) : (
                  appointment && (
                    <span className="rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                      RDV {hm(appointment.appointmentTime)}
                    </span>
                  )
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PatientSearchStep;
